/**
 * src/components/shared/TimeLogForm.jsx — Shared Time Log Form
 * AC Nautitech Design System
 *
 * Reusable time entry form used by TaskDetailPanel (inline).
 * Fully prop-driven — parent provides IDs, workTypes, and submission handler.
 */

import { useState, useRef } from 'react';
import { Clock, Save, RotateCcw } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { useWorkTypes } from '../../lib/lookups/LookupContext';
import { FALLBACK_WORK_TYPES } from '../../lib/constants/workflows';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAVE_TIMEOUT_MS = 10_000;

const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--bdr)',
  background: 'var(--card)', fontSize: 13, color: 'var(--t1)', outline: 'none', minHeight: 44,
};

export default function TimeLogForm({
  taskId,
  projectId,
  assigneeId,
  workTypes,
  onSubmit,
  onCancel,
  compact = false,
}) {
  const { t } = useT();
  const dbWorkTypes = useWorkTypes();
  const dbWorkTypeLabels = dbWorkTypes.length > 0
    ? dbWorkTypes.map(wt => wt.label_en || wt.slug)
    : FALLBACK_WORK_TYPES;
  const types = workTypes?.length ? workTypes : dbWorkTypeLabels;

  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [workType, setWorkType] = useState(types[0] || 'Production');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const abortRef = useRef(null);

  const validHours = hours && parseFloat(hours) > 0;
  const validNotes = notes.trim().length > 0;
  const validTaskId = taskId && UUID_RE.test(taskId);
  const canSave = validHours && !!date && validNotes && validTaskId;

  const handleSubmit = async () => {
    // Pre-save validation
    if (!validTaskId) { setError('Invalid task — cannot save time entry.'); return; }
    if (!validHours) { setError('Hours must be greater than 0.'); return; }
    if (!canSave) return;

    // Abort any in-flight request
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), SAVE_TIMEOUT_MS);

    setSaving(true);
    setError('');
    setTimedOut(false);
    try {
      const result = await onSubmit({
        task_id: taskId,
        project_id: projectId,
        person_id: assigneeId || undefined,
        duration_hours: parseFloat(hours),
        date,
        work_type: workType,
        notes: notes.trim(),
      }, { signal: controller.signal });
      clearTimeout(timeout);

      if (controller.signal.aborted) {
        setTimedOut(true);
        setError('Save timed out \u2014 your entry may not have been saved. Retry?');
        return;
      }

      if (result?.success) {
        setHours('');
        setNotes('');
        onCancel?.();
      } else {
        // Keep form data intact on error
        setError(result?.error || 'Failed to log time');
      }
    } catch (e) {
      clearTimeout(timeout);
      if (controller.signal.aborted || e.name === 'AbortError') {
        setTimedOut(true);
        setError('Save timed out \u2014 your entry may not have been saved. Retry?');
      } else {
        // Keep form data intact on error
        setError(e.message || 'Failed to save time entry');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 12 }}>
      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: 8,
          background: timedOut ? 'rgba(221,107,32,0.1)' : 'rgba(229,62,62,0.1)',
          color: timedOut ? 'var(--warn,#DD6B20)' : 'var(--bad)',
          fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          {(timedOut || error.includes('Failed')) && (
            <button
              onClick={handleSubmit}
              disabled={saving}
              type="button"
              style={{
                padding: '4px 10px', borderRadius: 6, border: 'none',
                background: 'var(--navy-surface)', color: 'white', fontSize: 11,
                fontWeight: 600, cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 4, minHeight: 44, whiteSpace: 'nowrap',
              }}
            >
              <RotateCcw size={11} /> Retry
            </button>
          )}
        </div>
      )}

      {compact ? (
        /* Compact: hours + date side by side */
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 2, display: 'block' }}>Hours *</label>
            <input type="number" step="0.25" min="0" value={hours}
              onChange={e => setHours(e.target.value)}
              placeholder="e.g. 2.5" style={inp} autoFocus />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 2, display: 'block' }}>Date</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, flex: 1 }} />
              <button onClick={() => setDate(new Date().toISOString().split('T')[0])} type="button" style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 44 }}>Today</button>
            </div>
          </div>
        </div>
      ) : (
        /* Full: separate rows */
        <>
          <div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 4 }}>Hours</div>
            <input type="number" step="0.25" min="0" value={hours}
              onChange={e => setHours(e.target.value)}
              placeholder="e.g. 2.5" style={inp} autoFocus />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 4 }}>Date</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, flex: 1 }} />
              <button onClick={() => setDate(new Date().toISOString().split('T')[0])} type="button" style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 44 }}>Today</button>
            </div>
          </div>
        </>
      )}

      <div>
        <label style={{ fontSize: 11, color: 'var(--tm)', marginBottom: compact ? 2 : 4, display: 'block' }}>
          Work Type
        </label>
        <select value={workType} onChange={e => setWorkType(e.target.value)}
          style={{ ...inp, cursor: 'pointer' }}>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div>
        <label style={{
          fontSize: 11, marginBottom: compact ? 2 : 4, display: 'block',
          color: validNotes ? 'var(--tm)' : 'var(--bad)',
          fontWeight: validNotes ? 400 : 600,
        }}>
          Description {!validNotes && '*'}
        </label>
        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder={t('forms.timeLogWork')} style={inp} />
      </div>

      {!canSave && (hours || notes) && (
        <div style={{ fontSize: 11, color: 'var(--warn,#DD6B20)' }}>
          {!validTaskId && 'Invalid task. '}
          {!validHours && 'Hours required. '}
          {!date && 'Date required. '}
          {!validNotes && 'Description required.'}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={saving || !canSave}
        style={{
          padding: compact ? '10px 14px' : 12, borderRadius: 10,
          background: canSave ? 'var(--navy-surface)' : 'var(--bdr)',
          color: canSave ? 'white' : 'var(--tm)',
          fontSize: compact ? 13 : 14, fontWeight: 600, border: 'none',
          cursor: canSave ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}
      >
        {compact ? <Save size={14} /> : <Clock size={16} />}
        {saving ? 'Saving...' : compact ? 'Save Time Entry' : 'Log Time'}
      </button>
    </div>
  );
}
